import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { updateUserProfile } from '../services/firestoreService';
import { fetchCountryLists } from '../utils/countryData';
import { AVAILABLE_LANGUAGES } from '../constants/profile';

/**
 * useProfileEdit - Shared hook for profile attribute editing logic.
 * Used by both Profile.js and SidebarProfile.js to avoid duplicating
 * edit state management, save handlers, and validation.
 */
export default function useProfileEdit(userData = {}) {
  const { currentUser } = useAuth();

  // Which section is currently being edited
  const [editingSection, setEditingSection] = useState(null);

  // Edit values for each section
  const [editBio, setEditBio] = useState(userData.bio || '');
  const [editSocialLinks, setEditSocialLinks] = useState(userData.socialLinks || { twitch: '', customLink: { name: '', link: '' } });
  const [editStreamingLanguages, setEditStreamingLanguages] = useState(userData.streamingLanguages || { primary: [], secondary: [] });
  const [editGenres, setEditGenres] = useState(userData.genres || []);
  const [editPlatforms, setEditPlatforms] = useState(userData.platforms || []);
  const [editRegion, setEditRegion] = useState(userData.region || { continent: '', country: '' });
  const [editMatureContent, setEditMatureContent] = useState(userData.matureContent || 'all-audiences');

  // Country data (lazy loaded)
  const [continentOptions, setContinentOptions] = useState([]);
  const [countriesByContinent, setCountriesByContinent] = useState({});
  const [isCountriesLoaded, setIsCountriesLoaded] = useState(false);
  const [countriesLoadError, setCountriesLoadError] = useState('');

  // Start editing a section — resets edit values to current userData
  const startEditing = (section) => {
    switch (section) {
      case 'bio':
        setEditBio(userData.bio || '');
        break;
      case 'social':
        setEditSocialLinks(userData.socialLinks || { twitch: '', customLink: { name: '', link: '' } });
        break;
      case 'languages':
        setEditStreamingLanguages(userData.streamingLanguages || { primary: [], secondary: [] });
        break;
      case 'genres':
        setEditGenres(userData.genres || []);
        break;
      case 'platforms':
        setEditPlatforms(userData.platforms || []);
        break;
      case 'region':
        setEditRegion(userData.region || { continent: '', country: '' });
        break;
      case 'content':
        setEditMatureContent(userData.matureContent || 'all-audiences');
        break;
      default:
        break;
    }
    setEditingSection(section);
  };

  const cancelEditing = () => {
    setEditingSection(null);
  };

  const saveSection = async (section, data) => {
    try {
      if (currentUser?.uid) {
        await updateUserProfile(currentUser.uid, data);
        setEditingSection(null);
      }
    } catch (error) {
      console.error('Error saving profile:', error);
    }
  };

  // Language helpers
  const handlePrimaryLanguageChange = (languageCode) => {
    setEditStreamingLanguages({
      ...editStreamingLanguages,
      primary: languageCode ? [languageCode] : []
    });
  };

  const handleSecondaryLanguageChange = (languageCode) => {
    const currentSecondary = editStreamingLanguages.secondary || [];
    if (languageCode && !currentSecondary.includes(languageCode) && currentSecondary.length < 5) {
      setEditStreamingLanguages({
        ...editStreamingLanguages,
        secondary: [...currentSecondary, languageCode]
      });
    }
  };

  const removeSecondaryLanguage = (languageCode) => {
    setEditStreamingLanguages({
      ...editStreamingLanguages,
      secondary: (editStreamingLanguages.secondary || []).filter(code => code !== languageCode)
    });
  };

  const getAvailableLanguages = (excludeList = []) => {
    return AVAILABLE_LANGUAGES.filter(lang => !excludeList.includes(lang.code));
  };

  const getLanguageName = (code) => {
    const lang = AVAILABLE_LANGUAGES.find(l => l.code === code);
    return lang ? lang.name : '';
  };

  // Social links validation
  const isSocialLinksValid = () => {
    const customLinkName = editSocialLinks.customLink?.name?.trim();
    const customLinkUrl = editSocialLinks.customLink?.link?.trim();
    if (customLinkName && !customLinkUrl) return false;
    if (customLinkUrl && !customLinkUrl.startsWith('https://')) return false;
    return true;
  };

  // Lazy load country/continent lists when editing region
  useEffect(() => {
    let ignore = false;
    if (editingSection === 'region' && !isCountriesLoaded) {
      fetchCountryLists()
        .then(({ continents, countriesByContinent }) => {
          if (ignore) return;
          setContinentOptions(continents);
          setCountriesByContinent(countriesByContinent);
          setIsCountriesLoaded(true);
        })
        .catch((err) => {
          if (ignore) return;
          console.error('Failed to load countries:', err);
          setCountriesLoadError('Failed to load countries');
        });
    }
    return () => { ignore = true; };
  }, [editingSection, isCountriesLoaded]);

  // Empty checks
  const isBioEmpty = !userData.bio;
  const isSocialEmpty = !userData.socialLinks?.twitch && !userData.socialLinks?.customLink?.link;
  const isLanguagesEmpty = !userData.streamingLanguages?.primary?.length && !userData.streamingLanguages?.secondary?.length;
  const isGenresEmpty = !userData.genres?.length;
  const isPlatformsEmpty = !userData.platforms?.length;
  const isRegionEmpty = !userData.region?.continent;
  const isContentEmpty = !userData.matureContent;

  return {
    editingSection,
    startEditing,
    cancelEditing,
    saveSection,

    // Edit states
    editBio, setEditBio,
    editSocialLinks, setEditSocialLinks,
    editStreamingLanguages, setEditStreamingLanguages,
    editGenres, setEditGenres,
    editPlatforms, setEditPlatforms,
    editRegion, setEditRegion,
    editMatureContent, setEditMatureContent,

    // Language helpers
    handlePrimaryLanguageChange,
    handleSecondaryLanguageChange,
    removeSecondaryLanguage,
    getAvailableLanguages,
    getLanguageName,

    // Social validation
    isSocialLinksValid,

    // Country data
    continentOptions,
    countriesByContinent,
    isCountriesLoaded,
    countriesLoadError,

    // Empty checks
    isBioEmpty,
    isSocialEmpty,
    isLanguagesEmpty,
    isGenresEmpty,
    isPlatformsEmpty,
    isRegionEmpty,
    isContentEmpty,
  };
}
